import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { firstValueFrom } from "rxjs";
import { Chart } from "chart.js";
import { SharedModule } from "../../../../shared/shared.module";
import { Widget } from "../../models/dashboard.model";
import { NC_STATS_DATA } from "../../models/data.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import { NonConformiteApiService } from "../../../non-conformite/services/non-conformite/non-conformite-api.service";
import { WidgetDataFacadeService } from "../../services/widget-data-facade.service";
import { NcStatsDialogComponent } from "./nc-stats-dialog.component";
import {
  periodBoundsFromTimeScale,
  toLocalIsoDay,
} from "../qualite-period-bounds.utils";

/**
 * Couleurs origine NC alignées sur la palette par défaut de Chart.js
 * (utilisée par les autres widgets graphiques comme `valeur-par-famille`
 * qui ne surchargent pas les couleurs). On garde une rotation simple :
 * bleu, rouge, orange, gris.
 */
export const NC_ORIGINE_COLORS = {
  interne: "#36A2EB",
  externe: "#FF6384",
  mixte: "#FF9F40",
  nonRenseigne: "#C9CBCF",
};


@Component({
  selector: "vl-widget-nc-stats",
  standalone: true,
  imports: [SharedModule, WidgetTitleComponent, ChipsComponent, WidgetPeriodFiltersComponent],
  host: { class: "widget-component" },
  templateUrl: "./widget-nc-stats.component.html",
  styleUrl: "./widget-nc-stats.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetNcStatsComponent implements OnDestroy {
  /**
   * `data` input optionnel — utilisé en mode "embedded" (page Analyse NC
   * qui pilote la période globalement). Si non fourni, le widget passe en
   * mode "standalone" et fait son propre fetch avec un filtre période.
   */
  data = input<NC_STATS_DATA>();
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  canvasRef = viewChild<ElementRef>("origineChart");
  chart: any;
  origineLabels = signal<{ label: string; percentage: string; color: string; count: number }[]>([]);

  private dialogsService = inject(DialogsService);
  private api = inject(NonConformiteApiService);
  private widgetDataFacade = inject(WidgetDataFacadeService);

  /**
   * Échelle temporelle alignée sur `vl-widget-period-filters` :
   *   "1 m" | "6 m" | "YTD" | "EX" | "1 a" | "5 a".
   * Default = YTD. Ignoré quand `data` input est fourni (mode embedded).
   */
  timeScale = signal<string>("YTD");

  /** Données fetchées en interne (mode standalone). */
  private internalData = signal<NC_STATS_DATA | null>(null);
  private internalLoading = signal(false);

  /** Données effectivement utilisées : input prioritaire, sinon internes. */
  displayData = computed(() => this.data() ?? this.internalData());

  /** Loading effectif : metadata input prioritaire, sinon interne. */
  isLoading = computed(() => this.metadata()?.loading ?? this.internalLoading());

  /** Filtres période visibles uniquement en mode standalone. */
  showPeriodFilter = computed(() => !this.data());

  hasData = computed(() => {
    const a = this.displayData()?.analytics;
    return !!a && a.totalCount > 0;
  });

  tauxEchappees = computed(() => {
    const v = this.displayData()?.stats?.tauxEchappees;
    return v == null ? "—" : `${v}%`;
  });

  origineSegments = computed(() => {
    const r = this.displayData()?.stats?.origineRepartition;
    if (!r) return [];
    return [
      { key: "interne", label: "nc_origine_interne", count: r.interne, color: NC_ORIGINE_COLORS.interne },
      { key: "externe", label: "nc_origine_externe", count: r.externe, color: NC_ORIGINE_COLORS.externe },
      { key: "mixte", label: "nc_origine_mixte", count: r.mixte, color: NC_ORIGINE_COLORS.mixte },
      {
        key: "nonRenseigne",
        label: "nc_origine_non_renseigne",
        count: r.nonRenseigne,
        color: NC_ORIGINE_COLORS.nonRenseigne,
      },
    ].filter((s) => s.count > 0);
  });

  constructor() {
    // Mode standalone : refetch quand la période change (et qu'il n'y a
    // pas d'input data forcée par un parent).
    effect(
      () => {
        const scale = this.timeScale();
        if (this.data()) return;
        this.fetchInternal(scale);
      },
      { allowSignalWrites: true },
    );

    // Self-fetching : on s'abonne au refreshTick global du dashboard pour
    // refetch quand l'utilisateur clique le bouton "Actualiser".
    let firstTick = true;
    effect(
      () => {
        const tick = this.widgetDataFacade.refreshTick();
        if (firstTick) {
          firstTick = false;
          return;
        }
        void tick;
        if (this.data()) return;
        this.fetchInternal(this.timeScale());
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const segs = this.origineSegments();
        if (segs.length && this.canvasRef()) {
          this.buildChart(segs);
        }
      },
      { allowSignalWrites: true },
    );
  }

  private async fetchInternal(scale: string) {
    this.internalLoading.set(true);
    try {
      const { start, end } = periodBoundsFromTimeScale(scale);
      const startStr = toLocalIsoDay(start);
      const endStr = toLocalIsoDay(end);

      const [stats, analytics] = await Promise.all([
        firstValueFrom(this.api.stats(startStr, endStr)),
        firstValueFrom(this.api.analytics(startStr, endStr)),
      ]);
      this.internalData.set({ stats, analytics });
    } finally {
      this.internalLoading.set(false);
    }
  }

  private buildChart(segs: { label: string; count: number; color: string }[]) {
    const canvas = this.canvasRef().nativeElement as HTMLCanvasElement;
    const ctx = canvas.getContext("2d");
    if (this.chart) {
      this.chart.destroy();
    }
    const total = segs.reduce((a, b) => a + b.count, 0);
    const labels = segs.map((s) => s.label);
    const data = segs.map((s) => s.count);
    const colors = segs.map((s) => s.color);

    this.chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor: "#fff",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        plugins: { legend: { display: false }, title: { display: false } },
      },
    });

    this.origineLabels.set(
      segs.map((s) => ({
        label: s.label,
        count: s.count,
        color: s.color,
        percentage: total > 0 ? Math.round((s.count / total) * 100).toString() : "0",
      })),
    );
  }

  openFullList() {
    if (!this.hasData()) return;
    this.dialogsService.open(
      NcStatsDialogComponent,
      "min(95vw, 920px)",
      "fit-content",
      { data: this.displayData() },
    );
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
